import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tooltip } from '@repo/design-system/components/ds/tooltip/tooltip';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { resetAllFilters, selectExternalMapId, selectFilterState, selectMapSupportsTeleport, setHideAll, toggleTeleportOnly, toggleUnfoundOnly } from '@repo/store/maps/slices/mapData';
import '../panda-conditions';
import { Button } from '@repo/design-system/components/ds/button';
const filterButtonsContainerStyles = css.raw({
  marginBottom: '12',
  flexWrap: 'nowrap',
  _compactFilters: {
    flexDirection: 'column',
    gap: '4',
    alignItems: 'stretch'
  }
});
const filterButtonStyles = css.raw({
  flexShrink: '1',
  minWidth: '[0]',
  padding: '6',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4',
  height: '[auto]',
  minHeight: '[48px]',
  _compactFilters: {
    flexDirection: 'row',
    justifyContent: 'center',
    minHeight: '[36px]',
    width: 'full'
  }
});
const mobileChipStyles = css.raw({
  flex: '1',
  minWidth: '[0]',
  paddingX: '8',
  paddingY: '8',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4',
  whiteSpace: 'nowrap',
  height: '[auto]',
  fontWeight: 'medium'
});
interface MapFiltersProps {
  isMobile?: boolean;
}
export const MapFilters: React.FC<MapFiltersProps> = t0 => {
  const $ = _c(36);
  const {
    isMobile: t1
  } = t0;
  const isMobile = t1 === undefined ? false : t1;
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilterState);
  const externalMapId = useAppSelector(selectExternalMapId);
  const mapSupportsTeleport = useAppSelector(selectMapSupportsTeleport);
  const hostType = useHostType();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["game_map"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useT(t2);
  const analytics = useAnalytics();
  let t3;
  if ($[1] !== analytics || $[2] !== dispatch || $[3] !== externalMapId || $[4] !== filters.hideAll || $[5] !== hostType) {
    t3 = () => {
      if (filters.hideAll) {
        dispatch(resetAllFilters());
      } else {
        dispatch(setHideAll(true));
      }
      if (analytics && externalMapId && hostType) {
        analytics.event("game_map_toggle_all_categories", {
          mapId: externalMapId,
          filtered: !filters.hideAll,
          platform: hostType
        });
      }
    };
    $[1] = analytics;
    $[2] = dispatch;
    $[3] = externalMapId;
    $[4] = filters.hideAll;
    $[5] = hostType;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleShowAllToggle = t3;
  let t4;
  if ($[7] !== analytics || $[8] !== dispatch || $[9] !== externalMapId || $[10] !== filters.teleportOnly || $[11] !== filters.unfoundOnly || $[12] !== hostType) {
    t4 = () => {
      dispatch(toggleTeleportOnly());
      if (analytics && externalMapId && hostType) {
        analytics.event("game_map_change_filters", {
          mapId: externalMapId,
          showTeleportOnly: !filters.teleportOnly,
          showUnfoundOnly: filters.unfoundOnly,
          platform: hostType
        });
      }
    };
    $[7] = analytics;
    $[8] = dispatch;
    $[9] = externalMapId;
    $[10] = filters.teleportOnly;
    $[11] = filters.unfoundOnly;
    $[12] = hostType;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleTeleportOnlyToggle = t4;
  let t5;
  if ($[14] !== analytics || $[15] !== dispatch || $[16] !== externalMapId || $[17] !== filters.teleportOnly || $[18] !== filters.unfoundOnly || $[19] !== hostType) {
    t5 = () => {
      dispatch(toggleUnfoundOnly());
      if (analytics && externalMapId && hostType) {
        analytics.event("game_map_change_filters", {
          mapId: externalMapId,
          showTeleportOnly: filters.teleportOnly,
          showUnfoundOnly: !filters.unfoundOnly,
          platform: hostType
        });
      }
    };
    $[14] = analytics;
    $[15] = dispatch;
    $[16] = externalMapId;
    $[17] = filters.teleportOnly;
    $[18] = filters.unfoundOnly;
    $[19] = hostType;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const handleUnfoundOnlyToggle = t5;
  const t6 = isMobile ? "0" : "4";
  let t7;
  if ($[21] !== t6) {
    t7 = css({
      padding: t6,
      width: "full"
    });
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== filters.hideAll || $[24] !== filters.teleportOnly || $[25] !== filters.unfoundOnly || $[26] !== handleShowAllToggle || $[27] !== handleTeleportOnlyToggle || $[28] !== handleUnfoundOnlyToggle || $[29] !== isMobile || $[30] !== mapSupportsTeleport || $[31] !== t) {
    t8 = isMobile ? <div className={css({
      display: "flex",
      gap: "6",
      paddingY: "2",
      width: "full"
    })}><Button onClick={handleShowAllToggle} variant={!filters.hideAll ? "ghost" : "filled"} color={filters.hideAll ? "white" : "primary"} size="sm" radius="rounded" className={css(mobileChipStyles)}><Icon icon={filters.hideAll ? "visibility" : "visibility_off"} decorative={true} /><Type variant="body-xs" color={!filters.hideAll ? "text.primary" : "black"}>{filters.hideAll ? t(_temp) : t(_temp2)}</Type></Button><Tooltip content={t(_temp3)} disabled={mapSupportsTeleport} placement="top" size="md"><Button onClick={handleTeleportOnlyToggle} variant={!filters.teleportOnly ? "ghost" : "filled"} color={filters.teleportOnly ? "white" : "primary"} size="sm" radius="rounded" disabled={!mapSupportsTeleport} className={css(mobileChipStyles)}><Icon icon="sprint" decorative={true} /><Type variant="body-xs" color={!filters.teleportOnly ? "text.primary" : "black"}>{t(_temp4)}</Type></Button></Tooltip><Button onClick={handleUnfoundOnlyToggle} variant={!filters.unfoundOnly ? "ghost" : "filled"} color={filters.unfoundOnly ? "white" : "primary"} size="sm" radius="rounded" className={css(mobileChipStyles)}><Icon icon="not_listed_location" decorative={true} /><Type variant="body-xs" color={!filters.unfoundOnly ? "text.primary" : "black"}>{t(_temp5)}</Type></Button></div> : <FlexRow gap="4" justify="between" className={filterButtonsContainerStyles}><Button onClick={handleShowAllToggle} variant={!filters.hideAll ? "ghost" : "filled"} color={filters.hideAll ? "white" : "primary"} size="sm" radius="rounded" className={css(filterButtonStyles)}><Icon icon={filters.hideAll ? "visibility" : "visibility_off"} decorative={true} /><Type variant="caption" color={!filters.hideAll ? "text.primary" : "black"} textAlign="center" className={css({
          lineHeight: "[1.1]"
        })}>{filters.hideAll ? t(_temp6) : t(_temp7)}</Type></Button><Tooltip content={t(_temp8)} disabled={mapSupportsTeleport} placement="top" size="md"><Button onClick={handleTeleportOnlyToggle} variant={!filters.teleportOnly ? "ghost" : "filled"} color={filters.teleportOnly ? "white" : "primary"} size="sm" radius="rounded" disabled={!mapSupportsTeleport} className={css(filterButtonStyles)}><Icon icon="sprint" decorative={true} /><Type variant="caption" color={!filters.teleportOnly ? "text.primary" : "black"} textAlign="center" className={css({
            lineHeight: "[1.1]"
          })}>{t(_temp9)}</Type></Button></Tooltip><Button onClick={handleUnfoundOnlyToggle} variant={!filters.unfoundOnly ? "ghost" : "filled"} color={filters.unfoundOnly ? "white" : "primary"} size="sm" radius="rounded" className={css(filterButtonStyles)}><Icon icon="not_listed_location" decorative={true} /><Type variant="caption" color={!filters.unfoundOnly ? "text.primary" : "black"} textAlign="center" className={css({
          lineHeight: "[1.1]"
        })}>{t(_temp10)}</Type></Button></FlexRow>;
    $[23] = filters.hideAll;
    $[24] = filters.teleportOnly;
    $[25] = filters.unfoundOnly;
    $[26] = handleShowAllToggle;
    $[27] = handleTeleportOnlyToggle;
    $[28] = handleUnfoundOnlyToggle;
    $[29] = isMobile;
    $[30] = mapSupportsTeleport;
    $[31] = t;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== t7 || $[34] !== t8) {
    t9 = <div className={t7}>{t8}</div>;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
};
function _temp($) {
  return $.show_all;
}
function _temp2($_0) {
  return $_0.hide_all;
}
function _temp3($_1) {
  return $_1.teleport_filter_disabled_tooltip;
}
function _temp4($_2) {
  return $_2.teleport_only;
}
function _temp5($_3) {
  return $_3.unfound_only;
}
function _temp6($_4) {
  return $_4.show_all;
}
function _temp7($_5) {
  return $_5.hide_all;
}
function _temp8($_6) {
  return $_6.teleport_filter_disabled_tooltip;
}
function _temp9($_7) {
  return $_7.teleport_only;
}
function _temp10($_8) {
  return $_8.unfound_only;
}